import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LogoFull, LogoMark } from '../components/Logo';
import { ArrowRight, ShieldCheck, Clock, FileCheck2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingDiagId = searchParams.get('diag');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await login(email.trim(), password);
      if (pendingDiagId) {
        // Usuario existente que viene del diagnóstico gratis: transferir el lead
        try { await api.post(`/public/diagnostic/${pendingDiagId}/claim`); } catch { /* ya reclamado o inválido */ }
      }
      toast.success('Bienvenido a Priviax');
      // Multi-org: si el usuario tiene 2+ organizaciones, mostrar selector
      if ((data.memberships?.length || 0) > 1) {
        navigate('/seleccionar-organizacion');
        return;
      }
      if (!data.organization?.onboarding_complete) {
        navigate('/onboarding');
      } else {
        navigate('/app/dashboard');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No pudimos iniciar sesión.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col p-8 lg:p-14">
        <div className="flex items-center justify-between">
          <LogoFull height={32} />
          <Link to="/registro" className="text-sm text-[#374151] hover:text-[#1C2329]" data-testid="link-register">
            ¿Sin cuenta? <span className="text-[#2A9B68] font-medium">Crear organización</span>
          </Link>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto lg:mx-0">
            <div className="pv-eyebrow mb-3">Ingresar</div>
            <h1 className="text-3xl md:text-4xl font-semibold text-[#1C2329] tracking-tight">
              Cumplimiento simple de la Ley 21.719.
            </h1>
            <p className="mt-3 text-[15px] text-[#6B7280] leading-relaxed">
              Diagnóstico en 15 minutos, plan de acción priorizado y evidencia auditable lista para la Agencia.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
              <div>
                <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Correo</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pv-input"
                  placeholder="nombre@empresa.cl"
                  autoComplete="email"
                  required
                  data-testid="login-email"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[12px] font-medium text-[#374151] uppercase tracking-wider">Contraseña</label>
                  <Link to="/forgot-password" className="text-[11.5px] text-[#143A34] hover:underline" data-testid="link-forgot">
                    ¿Olvidó su contraseña?
                  </Link>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pv-input"
                  placeholder="Su contraseña"
                  autoComplete="current-password"
                  required
                  data-testid="login-password"
                />
              </div>

              {error && (
                <div className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FCA5A5]/60 px-3 py-2 rounded-md" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="pv-btn-primary w-full flex items-center justify-center gap-2"
                data-testid="login-submit"
              >
                {submitting ? 'Ingresando…' : 'Ingresar a Priviax'}
                {!submitting && <ArrowRight size={16} strokeWidth={1.75} />}
              </button>
            </form>

            <div className="mt-6 text-[12px] text-[#6B7280]">
              Al ingresar acepta nuestros{' '}
              <Link to="/terminos" className="text-[#143A34] underline hover:no-underline">Términos y Condiciones</Link>
              {' '}y nuestra{' '}
              <Link to="/privacidad" className="text-[#143A34] underline hover:no-underline">Política de Privacidad</Link>.
            </div>
          </div>
        </div>

        <div className="text-[12px] text-[#6B7280]">© {new Date().getFullYear()} Priviax · Santiago, Chile.</div>
      </div>

      {/* Right: showcase */}
      <div className="hidden lg:flex bg-[#0B1220] text-white p-14 relative overflow-hidden">
        <div className="absolute inset-0 pv-hero-grid opacity-30 pointer-events-none" />
        <div className="relative z-10 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <LogoMark size={26} dark />
            <span className="text-[13px] text-white/70 uppercase tracking-widest">Panel de cumplimiento</span>
          </div>

          <div className="max-w-md space-y-6">
            <div className="pv-eyebrow text-[#7EE0AC]">Ley 21.719 · vigente 1 dic 2026</div>
            <h2 className="text-3xl font-semibold text-white tracking-tight">
              Convertimos una obligación legal compleja en un checklist accionable.
            </h2>
            <p className="text-[15px] text-white/70 leading-relaxed">
              Priviax es la plataforma que las empresas chilenas usan para llegar preparadas a las fiscalizaciones de la Agencia de Protección de Datos.
            </p>

            <div className="grid grid-cols-1 gap-3 pt-4">
              {[
                { icon: ShieldCheck, title: 'Score de cumplimiento 0–100', desc: 'Diagnóstico guiado, sin lenguaje jurídico.' },
                { icon: Clock, title: 'Plan priorizado', desc: 'Tareas con tiempo estimado y responsable.' },
                { icon: FileCheck2, title: 'Evidencia auditable', desc: 'Bitácora inmutable para fiscalizaciones.' },
              ].map((it) => (
                <div key={it.title} className="flex gap-3 items-start p-4 rounded-md bg-white/[0.04] border border-white/10">
                  <it.icon size={18} strokeWidth={1.5} className="text-[#7EE0AC] mt-0.5" />
                  <div>
                    <div className="text-[14px] font-medium text-white">{it.title}</div>
                    <div className="text-[13px] text-white/60 mt-0.5">{it.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[12px] text-white/40">
            Referentes de producto: OneTrust · Vanta · Iubenda · Linear.
          </div>
        </div>
      </div>
    </div>
  );
}
